import { create } from 'zustand';

/**
 * Where the bearer token lives between page loads.
 *
 * ADR 0002 records the trade-off: a token in localStorage is readable by
 * injected script, which we accept because the alternative (cookies) cannot
 * serve a native client from the same endpoints. The mitigations are a strict
 * CSP, no `dangerouslySetInnerHTML` anywhere, and short-lived tokens that the
 * user can revoke per device.
 */
/**
 * Exported for `session.test.ts`, which asserts the token really does land in
 * `localStorage` under this key — the whole "stay signed in after two days"
 * guarantee is that one write.
 */
export const TOKEN_STORAGE_KEY = 'thuiscoach.token';

interface SessionState {
    /** Null when signed out. */
    token: string | null;
    /**
     * True until the stored token has been checked against the API on boot.
     * Routing must wait for this, otherwise a refresh briefly bounces an
     * authenticated user to the login screen.
     */
    isRestoring: boolean;
    /**
     * True when the *API* ended the session rather than the user: the token
     * had expired (fourteen days after it was issued) or had been revoked from
     * another device. The login screen reads it and says so.
     *
     * Deliberately not persisted. It only ever has to survive from the 401 to
     * the redirect that immediately follows it, which is one page load — and a
     * flag in `localStorage` would outlive its own reason and greet the user
     * with a stale explanation days later.
     */
    wasEndedByApi: boolean;
    setToken: (token: string) => void;
    clear: () => void;
    expire: () => void;
    finishRestoring: () => void;
}

function readStoredToken(): string | null {
    try {
        return localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
        return null;
    }
}

function writeStoredToken(token: string | null): void {
    try {
        if (token === null) {
            localStorage.removeItem(TOKEN_STORAGE_KEY);
        } else {
            localStorage.setItem(TOKEN_STORAGE_KEY, token);
        }
    } catch {
        // Private browsing: the session simply does not survive a reload.
    }
}

const storedToken = readStoredToken();

export const useSessionStore = create<SessionState>((set) => ({
    token: storedToken,
    // With no stored token there is nothing to restore, so routing can proceed
    // immediately rather than flashing a loading screen.
    isRestoring: storedToken !== null,
    wasEndedByApi: false,

    setToken: (token) => {
        writeStoredToken(token);
        set({ token, isRestoring: false, wasEndedByApi: false });
    },

    clear: () => {
        writeStoredToken(null);
        set({ token: null, isRestoring: false, wasEndedByApi: false });
    },

    // Same as `clear`, plus the reason. Signing out yourself and being signed
    // out by the server end in the same place, and only one of them owes the
    // reader an explanation.
    expire: () => {
        writeStoredToken(null);
        set({ token: null, isRestoring: false, wasEndedByApi: true });
    },

    finishRestoring: () => {
        set({ isRestoring: false });
    },
}));

/**
 * Reads the token outside React.
 *
 * The API client is not a component and must not subscribe to the store, so it
 * pulls the current value at request time.
 */
export function currentToken(): string | null {
    return useSessionStore.getState().token;
}

/**
 * Drops the session after the API reports the token is no longer valid.
 *
 * Used by the API client's 401 middleware, which is not a component either.
 * It calls `expire`, not `clear`: the difference is what the login screen is
 * allowed to say when the user arrives there.
 */
export function endSession(): void {
    useSessionStore.getState().expire();
}
